import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography, useMediaQuery } from "@mui/material";
import Dashboard from "../../pages/dashboard/Dashboard";
import PatientProfile from "../../pages/dashboard/PatientProfile";
import FormsList from "../../pages/dashboard/forms";

/* --------------------------------
   Main Sidebar Component
---------------------------------- */

const LeftSidebarMenu = ({ toggleActive, active }) => {
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:1199.95px)");

  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const checkDarkTheme = () => {
      const htmlHasDark = document.documentElement.classList.contains("dark-theme");
      setIsDark(htmlHasDark);
    };

    checkDarkTheme();

    const observer = new MutationObserver(checkDarkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  /* ✅ Shared style for all module rows (matches previous accordion header style) */
  const moduleStyle = {
    display: "flex",
    alignItems: "center",
    minHeight: "52px",
    padding: "0 16px",
    borderRadius: "10px",
    transition: "all 0.2s ease",
    textDecoration: "none",
    color: isDark ? "#fff" : "#374151",
    fontSize: "0.975rem",
    fontWeight: 600,
    letterSpacing: "0.3px",
    cursor: "pointer",
    marginBottom: "8px",
    "&:hover": {
      backgroundColor: "rgba(86, 81, 244, 0.06)",
      "& .module-icon": {
        color: "#5651F4",
      },
    },
  };

  const moduleActiveStyle = {
    backgroundColor: "rgba(86, 81, 244, 0.1)",
    color: "#5651F4",
    "& .module-icon": {
      color: "#5651F4",
    },
  };

  /* ✅ Reusable Module Item — with mobile auto-close */
  const ModuleItem = ({ to, icon, label }) => {
    const isActive = location.pathname.toLowerCase() === to.toLowerCase();

    const handleModuleClick = () => {
      // ✅ Auto close sidebar only on mobile
      if (isMobile && active && toggleActive) {
        toggleActive();
      }
    };

    return (
      <Box
        component={Link}
        to={to}
        onClick={handleModuleClick}
        sx={{
          ...moduleStyle,
          ...(isActive && moduleActiveStyle),
        }}
      >
        <i
          className="material-symbols-outlined module-icon"
          style={{
            marginRight: "12px",
            fontSize: "1.4rem",
            display: "flex",
            alignItems: "center",
            color: isActive
              ? "#5651F4"
              : isDark
                ? "rgba(255,255,255,0.7)"
                : "#6B7280",
            transition: "color 0.2s ease",
          }}
        >
          {icon}
        </i>
        <Typography
          component="span"
          sx={{
            flex: 1,
            fontSize: "0.975rem",
            fontWeight: 600,
            letterSpacing: "0.3px",
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {/* ✅ Dark overlay on mobile when sidebar is open */}
      {isMobile && active && (
        <Box
          onClick={toggleActive}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1199,
            opacity: active ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      <Box
        className={`leftSidebarDark hide-for-horizontal-nav ${isDark ? "dark-theme" : ""}`}
        sx={{
          width: "270px",
          minWidth: "270px",
          height: "100vh",
          position: "fixed",

          top: 0,
          left: { xs: active ? 0 : "-270px", lg: active ? "-270px" : 0 },
          zIndex: 1200,
          backgroundColor: isDark ? "#1a1a2e" : "#FFFFFF",
          borderRight: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F3F4F6",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",

          /* ✅ Smooth slide transition for mobile */
          transition: "left 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease",

          /* ✅ Shadow when open on mobile */
          ...(isMobile && active && {
            boxShadow: "4px 0 24px rgba(0, 0, 0, 0.15)",
          }),

          /* Scrollbar styling */
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#D1D5DB",
            borderRadius: "4px",
          },

          // ✅ DARK THEME STYLES
          ...(isDark && {
            "& .sidemenu-link": {
              color: "rgba(255,255,255,0.7) !important",
              fontSize: "0.935rem !important",
              padding: "10px 16px !important",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              fontWeight: 400,
            },
            "& .sidemenu-link:hover": {
              color: "#fff !important",
              backgroundColor: "rgba(255, 255, 255, 0.08) !important",
            },
            "& .sidemenu-link.active": {
              color: "#fff !important",
              backgroundColor: "rgba(86, 81, 244, 0.2) !important",
              borderLeft: "3px solid #5651F4 !important",
              fontWeight: 600,
            },
            "& .mat-summary .title": {
              color: "#fff !important",
              fontSize: "0.975rem !important",
              fontWeight: 600,
              letterSpacing: "0.3px",
            },
            "& .mat-summary i": {
              color: "rgba(255,255,255,0.7) !important",
              fontSize: "1.4rem !important",
            },
            "& .mat-summary .trezo-badge": {
              color: "#fff !important",
              backgroundColor: "rgba(86,81,244,0.3) !important",
              fontSize: "0.75rem !important",
              padding: "3px 8px !important",
              borderRadius: "12px",
              fontWeight: 600,
              minWidth: "24px",
              textAlign: "center",
            },
          }),

          // ✅ LIGHT THEME STYLES
          ...(!isDark && {
            "& .sidemenu-link": {
              fontSize: "0.935rem",
              padding: "10px 16px",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              fontWeight: 400,
              color: "#6B7280",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            },
            "& .sidemenu-link:hover": {
              backgroundColor: "rgba(86, 81, 244, 0.06)",
              color: "#5651F4",
            },
            "& .sidemenu-link.active": {
              backgroundColor: "rgba(86, 81, 244, 0.1)",
              borderLeft: "3px solid #5651F4",
              fontWeight: 600,
              color: "#5651F4",
            },
            "& .mat-summary .title": {
              fontSize: "0.975rem",
              fontWeight: 600,
              letterSpacing: "0.3px",
              color: "#374151",
            },
            "& .mat-summary i": {
              fontSize: "1.4rem",
              color: "#6B7280",
            },
            "& .trezo-badge": {
              fontSize: "0.75rem",
              padding: "3px 8px",
              borderRadius: "12px",
              backgroundColor: "#EEF2FF",
              color: "#5651F4",
              fontWeight: 600,
              minWidth: "24px",
              textAlign: "center",
              lineHeight: "1.4",
            },
          }),
        }}
      >
        <Box className="left-sidebar-menu" sx={{
          display: "flex", flexDirection: "column", height: "100%", position: "static !important",
          width: "100% !important",
          top: "auto !important",
          backgroundColor: "transparent !important",
          transition: "none !important",
          zIndex: "auto !important",
        }}>
          {/* ---------- LOGO ---------- */}
          <Box
            sx={{
              // ✅ BUG FIX #2 — Removed height: "25%" which was too tall on small screens
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #F3F4F6",
              marginBottom: "0px",
              flexShrink: 0,
            }}
          >
            <Link
              to="/dashboard/Dashboard"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src="/images/newlogo2.png"
                  alt="logo-icon"
                  // ✅ BUG FIX #2 — Responsive image size instead of 100% which overflows
                  style={{
                    height: "auto",
                    width: "clamp(80px, 60%, 110px)",
                    objectFit: "contain",
                  }}
                />
                {/* ✅ BUG FIX #2 — Removed marginBottom:"80px" and marginTop:"-40px" */}
                <h4
                  style={{
                    margin: "8px 0 4px 0",
                    fontSize: "clamp(0.75rem, 2vw, 0.9rem)",
                    textAlign: "center",
                    color: isDark ? "#fff" : "#374151",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "180px",
                  }}
                >
                  TruEvv Patient Portal
                </h4>
              </div>
            </Link>

            {/* Burger menu inline with logo */}
            <Box
              className="burger-menu"
              onClick={toggleActive}
              sx={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                flexShrink: 0,
                marginLeft: "12px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(239, 68, 68, 0.08)",
                  "& .close-icon": {
                    color: "#EF4444 !important",
                  },
                },
              }}
            >
              {/* ✅ BUG FIX #3 — Show ONLY close icon, hide hamburger bars inside sidebar */}
              <i
                className="material-symbols-outlined close-icon"
                style={{
                  fontSize: "1.4rem",
                  color: isDark ? "rgba(255,255,255,0.6)" : "#9CA3AF",
                  transition: "color 0.2s ease",
                }}
              >
                close
              </i>
            </Box>
          </Box>


          {/* ---------- SIDEBAR CONTENT ---------- */}
          <Box
            className="sidebar-inner"
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              marginTop: "0px",
              padding: "0 8px",
            }}
          >
            <Box className="sidebar-menu">

              {/* ✅ 1️⃣ DASHBOARD — Separate Module */}

              <ModuleItem
                to="/dashboard/Dashboard"
                icon="dashboard"
                label="Dashboard"
              />


              {/* ✅ 3️⃣ FORMS — Separate Module */}
              <ModuleItem
                to="/dashboard/forms"
                icon="description"
                label="Forms"
              />


              {/* ✅ 2️⃣ PATIENT PROFILE — Separate Module */}
              <ModuleItem
                to="/dashboard/patient-Profile"
                icon="account_circle"
                label="My Profile"
              />



            </Box>
          </Box>

          {/* ---------- SIDEBAR FOOTER ---------- */}
          <Box
            sx={{
              padding: "16px 20px",
              borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #F3F4F6",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: isDark ? "rgba(255,255,255,0.3)" : "#9CA3AF",
                textAlign: "center",
              }}
            >
              © 2025 NeuroEZ
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LeftSidebarMenu;